import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, 
  Users, 
  Trash2, 
  Edit, 
  Shield,
  UserCheck,
  UserX,
  Mail,
  Copy
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useUpgradePrompt, detectPlanLimitError } from '@/components/UpgradePromptDialog';
import InviteCodesCard from '@/components/InviteCodesCard';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'property_manager', label: 'Property Manager', color: 'bg-purple-500', description: 'Full access' },
  { value: 'owner', label: 'Owner', color: 'bg-blue-500', description: 'View properties & reports' },
  { value: 'cleaner', label: 'Cleaner', color: 'bg-emerald-500', description: 'Cleaning tasks' },
  { value: 'maintenance', label: 'Maintenance', color: 'bg-amber-500', description: 'Maintenance tasks' },
  { value: 'landscaping', label: 'Landscaping', color: 'bg-green-500', description: 'Landscaping tasks' },
  { value: 'repair', label: 'Repair', color: 'bg-red-500', description: 'Repair tasks' },
];

const RoleBadge = ({ role }) => {
  const roleInfo = ROLE_OPTIONS.find(r => r.value === role) || ROLE_OPTIONS[2];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${roleInfo.color}`}>
      {roleInfo.label}
    </span>
  );
};

export const UsersView = () => {
  const { user: currentUser, canManageUsers } = useAuth();
  const { showUpgradePrompt } = useUpgradePrompt();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'cleaner',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    role: '',
    is_active: true,
  });

  const [inviteData, setInviteData] = useState({
    email: '',
    name: '',
    role: 'cleaner',
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/api/users`, formData, {
        withCredentials: true
      });
      toast.success('User created successfully');
      setDialogOpen(false);
      setFormData({ email: '', password: '', name: '', role: 'cleaner' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteData.email || !inviteData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/users/invite`, inviteData, {
        withCredentials: true
      });
      setInviteResult(response.data);
      toast.success('Staff invited! Share the temporary password.');
      setInviteData({ email: '', name: '', role: 'cleaner' });
      fetchUsers();
    } catch (error) {
      const resource = detectPlanLimitError(error);
      if (resource) {
        showUpgradePrompt({ resource, detail: error.response?.data?.detail });
        setInviteDialogOpen(false);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to invite staff');
      }
    }
  };

  const copyTempPassword = () => {
    if (inviteResult?.temp_password) {
      navigator.clipboard.writeText(inviteResult.temp_password);
      toast.success('Temporary password copied to clipboard');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/api/users/${userToEdit.id}`, editFormData, {
        withCredentials: true
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setUserToEdit(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/api/users/${userToDelete.id}`, {
        withCredentials: true
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const openEditDialog = (user) => {
    setUserToEdit(user);
    setEditFormData({
      name: user.name,
      role: user.role,
      is_active: user.is_active !== false,
    });
    setEditDialogOpen(true);
  };

  if (!canManageUsers()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Only Property Managers can manage users</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            User Management
          </h1>
          <p className="text-slate-500 mt-1">
            Manage staff accounts and permissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => { setInviteDialogOpen(true); setInviteResult(null); }}
            variant="outline"
            data-testid="invite-staff-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Invite Staff
          </Button>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-user-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
              </div>
              <Users className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {users.filter(u => u.is_active !== false).length}
                </p>
              </div>
              <UserCheck className="h-8 w-8 text-emerald-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Inactive</p>
                <p className="text-2xl font-bold text-red-600">
                  {users.filter(u => u.is_active === false).length}
                </p>
              </div>
              <UserX className="h-8 w-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Staff</p>
                <p className="text-2xl font-bold text-blue-600">
                  {users.filter(u => ['cleaner', 'maintenance', 'landscaping', 'repair'].includes(u.role)).length}
                </p>
              </div>
              <Shield className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Users</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    user.is_active === false ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-100'
                  }`}
                  data-testid={`user-${user.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                      user.is_google_user ? 'bg-blue-500' : 'bg-slate-600'
                    }`}>
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        user.name?.charAt(0)?.toUpperCase() || 'U'
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{user.name}</span>
                        <RoleBadge role={user.role} />
                        {user.is_google_user && (
                          <span className="text-xs text-slate-400">(Google)</span>
                        )}
                        {user.is_active === false && (
                          <span className="text-xs text-red-500">(Inactive)</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  
                  {user.id !== currentUser?.id && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(user)}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUserToDelete(user);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Add New User</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                required
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                required
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                required
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Role *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded ${role.color}`}></span>
                        {role.label}
                        <span className="text-xs text-slate-400">- {role.description}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="user-submit-btn">
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Edit User</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                data-testid="edit-user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={editFormData.role}
                onValueChange={(value) => setEditFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded ${role.color}`}></span>
                        {role.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={editFormData.is_active}
                onChange={(e) => setEditFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="is_active">Active account</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="edit-user-submit-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{userToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Staff Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={(open) => { setInviteDialogOpen(open); if (!open) setInviteResult(null); }}>
        <DialogContent className="sm:max-w-[460px]" data-testid="invite-staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Invite Staff</DialogTitle>
          </DialogHeader>

          {inviteResult ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-medium text-emerald-900 mb-2">
                  Invite created for {inviteResult.email}
                </p>
                <p className="text-xs text-emerald-700">
                  {inviteResult.email_sent
                    ? 'An invitation email has been sent.'
                    : 'Email could not be delivered (likely Resend test mode). Share the temporary password below.'}
                </p>
              </div>
              <div className="space-y-1">
                <Label>Temporary Password</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={inviteResult.temp_password}
                    data-testid="invite-temp-password"
                    className="font-mono"
                  />
                  <Button type="button" variant="outline" size="icon" onClick={copyTempPassword} data-testid="copy-temp-password-btn">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Ask {inviteResult.name} to log in with this password and change it.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" onClick={() => { setInviteDialogOpen(false); setInviteResult(null); }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-name">Name *</Label>
                <Input
                  id="invite-name"
                  value={inviteData.name}
                  onChange={(e) => setInviteData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Jane Cleaner"
                  required
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email *</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="jane@example.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  value={inviteData.role}
                  onValueChange={(value) => setInviteData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.filter(r => r.value !== 'property_manager').map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded ${role.color}`}></span>
                          {role.label}
                          <span className="text-xs text-slate-400">- {role.description}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">
                A temporary password will be generated and emailed to them. You&apos;ll also see it
                here so you can share it manually.
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="invite-submit-btn">
                  Send Invite
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {currentUser?.role === 'property_manager' && <InviteCodesCard />}
    </div>
  );
};

export default UsersView;
